import React, { useCallback, useState, useRef, useLayoutEffect, useMemo } from 'react';
import { useSharedValue } from 'react-native-reanimated';
import { View, Alert, StyleSheet, Linking } from 'react-native';
import Logo from 'design-system/Logo';
import Text from 'design-system/Text';
import StatusBar from 'design-system/StatusBar';
import { IconEmail, IconUnion } from 'design-system/icons';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { Button, SettingsSection, NavigationHeader, SettingsItemInput, rem, AppView, Spacer, CardView, } from 'design-system';
import Links from 'utils/links';
import Sentry from 'utils/sentry';
import useColors from 'hooks/useColors';
import useOpenEmailApp from 'hooks/useOpenEmailApp';
import { registerUser } from 'actions/user';
import { selectUser } from 'reducers/selectors';
import { validateNIN } from 'utils/validation';
import { OnboardingFlow } from 'utils/types/navigation';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { muteErrorAndResultButThrows, useFetchOld } from 'hooks';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import useNavigateToCreatePot from 'features/pots/hooks/useNavigateToCreatePot';
import useNavigateToNextScreenForPotsOnboarding from 'features/pots/hooks/useNavigateToNextScreenForPotsOnboarding';
import { selectIsPotsAccountActive } from 'features/pots/reducers/selectors';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import { openApp } from 'utils/packages/intentLauncher';
import { useBottomOffset } from 'hooks/useBottomOffset';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import Squircle from 'design-system/Squircle';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import { isIOS } from '../../../constants';
import { useCommonInvestStyles } from './styles';
import { getRequiredNationalIdentifiers } from '../actions';
import { useAmplitudeOnMountEvent } from '../utils/analytics';
import { selectIsHmrcAppInstalled } from '../reducers/selectors';
import useNavigateToNextScreen from '../hooks/useNavigateToNextScreen';
import { HMRC_APP_URL_SCHEME, HMRC_APP_ANDROID_PACKAGE_NAME } from '../utils';
import { strings as onboardingTitleStrings } from '../components/OnboardingTitle';
const strings = {
    alertTitle: 'Hold on 💫',
    alertInvalidInsuranceNumber: 'You need to enter a valid UK National Insurance Number.',
    nationalInsuranceNumber: 'National Insurance number',
    placeholder: 'Add..',
    openTheApp: 'Open the app',
    openTheWebsite: 'Open the website',
    findInApp: 'Find it in the HMRC app',
    findInAccount: 'Find it in your HMRC account',
    helpSectionTitle: 'Can’t find your National Insurance number?',
    findInPayslip: 'Find it on your payslip or P60',
    openEmail: 'Open your email',
};
const hmrcLogo = require('../img/hmrc-logo.png');
const InsuranceScreen = ({ route: { params }, navigation }) => {
    const colors = useColors();
    const store = useAppStore();
    const dispatch = useAppDispatch();
    const commonStyles = useCommonInvestStyles(params?.mountedOnAnotherScreen);
    const flow = params?.flow;
    const productId = params?.productId;
    const mountedOnAnotherScreen = params?.mountedOnAnotherScreen;
    const isPotsFlow = flow === OnboardingFlow.Pots || flow === OnboardingFlow.Wallet;
    const bottomOffset = useBottomOffset();
    const [handleOpenEmail, hasEmailApps] = useOpenEmailApp();
    const { paddingHorizontalStyle } = useAppFrameDimensions(!!mountedOnAnotherScreen);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: '',
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const user = useAppSelector(selectUser);
    const isHmrcAppInstalled = useAppSelector(selectIsHmrcAppInstalled);
    const navigateToNextScreen = useNavigateToNextScreen();
    const navigateToNextScreenForPots = useNavigateToNextScreenForPotsOnboarding();
    const navigateToCreatePot = useNavigateToCreatePot();
    const [nationalInsuranceNumber, setNationalInsuranceNumber] = useState(() => {
        if (user?.taxId) {
            if (user.taxId.match(/^[A-Z]{2}\d{6}[A-Z]$/)) {
                return user.taxId;
            }
        }
        return '';
    });
    const nationalInsuranceNumberRef = useRef(null);
    const [, , loading, fetch] = useFetchOld(muteErrorAndResultButThrows);
    const scrollY = useSharedValue(0);
    useAmplitudeOnMountEvent(flow, !!mountedOnAnotherScreen, 'User Tax Screen');
    const onSubmit = useCallback(async () => {
        const trimmedNin = nationalInsuranceNumber.replace(/\s/g, '').toUpperCase();
        if (!validateNIN(trimmedNin)) {
            Alert.alert(strings.alertTitle, strings.alertInvalidInsuranceNumber);
        }
        else {
            nationalInsuranceNumberRef.current?.blur();
            try {
                if (trimmedNin !== user?.taxId) {
                    await fetch(registerUser({
                        taxId: trimmedNin,
                        countryOfTaxResidence: 'GBR',
                    }));
                }
                // want to cache the response here
                const response = await fetch(getRequiredNationalIdentifiers());
                if (isPotsFlow) {
                    /**
                     * User already has a pots account and came here from SelectPotType to add a NIN
                     * (e.g. for a Cash ISA), so continue creating that pot rather than continuing onboarding
                     */
                    if (selectIsPotsAccountActive(store.getState())) {
                        if (productId) {
                            navigateToCreatePot(productId, { replace: true });
                        }
                        else {
                            navigation.goBack();
                        }
                    }
                    else {
                        // case for onboarding
                        navigateToNextScreenForPots();
                    }
                    return;
                }
                const country = response.nationality;
                const requiredNationalIdentifiers = response.requiredNationalityIdentifiers;
                /**
                 * Initializing onboardingState if not present
                 */
                dispatch({ type: 'SAVE_ONBOARDING_STATE', screen: 'InvestInsurance', params: {} });
                if (requiredNationalIdentifiers?.length) {
                    navigation.navigateDeprecated('InvestExtraInsurance', {
                        identifier: { ...requiredNationalIdentifiers[0], country },
                    });
                }
                else {
                    // case of invest onboarding
                    navigateToNextScreen();
                }
            }
            catch {
                // useFetch never throws
            }
        }
    }, [
        nationalInsuranceNumber,
        navigateToNextScreen,
        navigateToNextScreenForPots,
        navigateToCreatePot,
        isPotsFlow,
        productId,
        user?.taxId,
    ]);
    const onChangeNin = useCallback((text) => {
        setNationalInsuranceNumber(text.replace(' ', ''));
    }, []);
    const openHMRCWebsite = useCallback(() => {
        Links.openLink('https://www.tax.service.gov.uk/gg/sign-in?continue=/personal-account/national-insurance-summary/print-letter');
    }, []);
    const handleOpenHMRC = useCallback(async () => {
        if (isHmrcAppInstalled) {
            try {
                if (isIOS) {
                    await Linking.openURL(`${HMRC_APP_URL_SCHEME}://`);
                    return;
                }
                if (await openApp(HMRC_APP_ANDROID_PACKAGE_NAME)) {
                    return;
                }
            }
            catch (err) {
                Sentry.logError({
                    err,
                    filename: 'InsuranceScreen',
                    message: 'Failed to open HMRC app',
                });
            }
        }
        openHMRCWebsite();
    }, [isHmrcAppInstalled, openHMRCWebsite]);
    const outputColors = useMemo(() => [colors.background.dark, colors.cards.onDark], [colors.background.dark, colors.cards.onDark]);
    const HelpSection = useMemo(() => {
        if (isHmrcAppInstalled) {
            return (<TouchableBounceCard noMarginTop noMarginBottom withPadding onDark onPress={handleOpenHMRC}>
          <View style={styles.row}>
            <Logo R44 uri={hmrcLogo}/>
            <View style={styles.flex}>
              <Text Text-16>{strings.findInApp}</Text>
              <Text TextThin-14 Primary>
                {strings.openTheApp}
              </Text>
            </View>
          </View>
        </TouchableBounceCard>);
        }
        return (<CardView noMarginTop noMarginBottom>
        <TouchableHighlight underlayColor={colors.background.underlay} onCard onPress={handleOpenHMRC}>
          <View style={styles.helpRow}>
            <Logo R44 uri={hmrcLogo}/>
            <View style={styles.flex}>
              <Text Text-16>{strings.findInAccount}</Text>
              <Text TextThin-14 Primary>
                {strings.openTheWebsite}
              </Text>
            </View>
          </View>
        </TouchableHighlight>
        {hasEmailApps ? (<TouchableHighlight underlayColor={colors.background.underlay} onCard onPress={handleOpenEmail}>
            <View style={styles.helpRow}>
              <Squircle color={colors.background.dark}>
                <IconEmail gradientKey="skyBlue"/>
              </Squircle>
              <View style={styles.flex}>
                <Text Text-16>{strings.findInPayslip}</Text>
                <Text TextThin-14 Primary>
                  {strings.openEmail}
                </Text>
              </View>
            </View>
          </TouchableHighlight>) : null}
      </CardView>);
    }, [
        isHmrcAppInstalled,
        colors.background.underlay,
        colors.background.dark,
        handleOpenHMRC,
        handleOpenEmail,
        hasEmailApps,
    ]);
    return (<AppView withFrame={false} pointerEvents={loading ? 'none' : 'auto'} isInTab={!!mountedOnAnotherScreen}>
      <StatusBar barStyle="dark-content" animated/>
      <NavigationHeader scrollY={scrollY} hideTitleOnScroll regularTitleHeight outputColors={outputColors} isInTab={!!mountedOnAnotherScreen} leftContent={mountedOnAnotherScreen ? <LeaveOnboardingButton flow={flow}/> : undefined} rightContent={<View style={!!mountedOnAnotherScreen && commonStyles.flexEnd}>
            <HelpScreenButton articlesKey={isPotsFlow
                ? HELP_ARTICLES_KEY.NATIONAL_INSURANCE_NUMBER_CASH_ISA
                : HELP_ARTICLES_KEY.NATIONAL_INSURANCE_NUMBER}/>
          </View>} title={onboardingTitleStrings.Insurance.shortTitle}/>
      <View style={[styles.flex, paddingHorizontalStyle]}>
        <ScreenInfoHeader title={onboardingTitleStrings.Insurance.title} subTitle={onboardingTitleStrings.Insurance.subtitle}/>
        <SettingsSection noMarginTop>
          <SettingsItemInput maxLength={9} testID="ninField" ref={nationalInsuranceNumberRef} value={nationalInsuranceNumber} description={strings.nationalInsuranceNumber} icon={<IconUnion />} onChangeText={onChangeNin} onClearTextInput={() => {
            if (!user.kycStartedAt || !user?.taxId) {
                setNationalInsuranceNumber('');
                if (nationalInsuranceNumberRef.current) {
                    nationalInsuranceNumberRef.current.focus();
                }
            }
        }} autoCapitalize="characters" autoCorrect={false} onSubmitEditing={onSubmit} editable={!user.kycStartedAt || !user?.taxId} placeholder={strings.placeholder}/>
        </SettingsSection>
        <View style={styles.flex}/>
        <Spacer h={24}/>
        <View style={styles.falseSectionContainer}>
          <Text TextThin-16 Secondary>
            {strings.helpSectionTitle}
          </Text>
        </View>
        {HelpSection}
        <Spacer h={24}/>
        <View style={styles.horizontalPadding}>
          <Button brand title="Continue" onPress={onSubmit} isFetching={loading}/>
        </View>
        <Spacer h={mountedOnAnotherScreen ? rem(16) : bottomOffset}/>
      </View>
    </AppView>);
};
const styles = StyleSheet.create({
    row: {
        gap: rem(16),
        alignItems: 'center',
        flexDirection: 'row',
    },
    helpRow: {
        gap: rem(16),
        padding: rem(16),
        alignItems: 'center',
        flexDirection: 'row',
    },
    flex: {
        flex: 1,
    },
    horizontalPadding: {
        paddingHorizontal: rem(16),
    },
    falseSectionContainer: {
        paddingTop: rem(24),
        paddingBottom: rem(16),
        paddingHorizontal: rem(16),
    },
});
export default InsuranceScreen;
